// @react-pdf stylesheet for the MW (TriloChat) PDF exports, kept alongside
// pdfStyles.js (the wizard-PDF stylesheet) so all PDF styling lives in one place.
//
// Unlike pdfStyles.js this is a factory, for two reasons: the body font is
// resolved at runtime by utils/pdfFont.js (Public Sans, or Helvetica if it
// cannot be embedded), and the export imports @react-pdf/renderer lazily - so
// `StyleSheet` is passed in rather than imported here.

export const TRILOCHAT_PDF_COLORS = {
  teal: "#4ab8aa",
  tealDark: "#3d9f93",
  tealTint: "#f7fdfc",
  blue: "#474eff",
  line: "#e5e7eb",
  text: "#1a1a2e",
  muted: "#9ca3af",
  amber: "#d97706",
  white: "#ffffff",
};

export const createTriloChatPdfStyles = (StyleSheet, bodyFont) => {
  const { teal, tealDark, tealTint, blue, line, text, muted, amber, white } =
    TRILOCHAT_PDF_COLORS;

  return StyleSheet.create({
    page: {
      paddingTop: 60,
      paddingBottom: 56,
      paddingLeft: 50,
      paddingRight: 50,
      fontFamily: bodyFont,
      fontSize: 10,
      color: text,
      lineHeight: 1.55,
    },
    header: {
      position: "absolute",
      top: 16,
      left: 50,
      right: 50,
      flexDirection: "row",
      justifyContent: "flex-end",
      borderBottomWidth: 0.5,
      borderBottomColor: line,
      paddingBottom: 6,
    },
    logo: { height: 18, objectFit: "contain" },
    footer: {
      position: "absolute",
      bottom: 18,
      left: 50,
      right: 50,
      borderTopWidth: 0.5,
      borderTopColor: line,
      paddingTop: 4,
      flexDirection: "row",
      justifyContent: "space-between",
    },
    footerText: { fontSize: 7, color: muted },
    sectionLabel: {
      fontSize: 8,
      fontFamily: bodyFont,
      fontWeight: "bold",
      color: teal,
      letterSpacing: 1.2,
      marginTop: 22,
      marginBottom: 10,
    },
    draftCard: {
      borderWidth: 1,
      borderColor: teal,
      borderRadius: 4,
      padding: "10pt 14pt",
      marginBottom: 14,
      backgroundColor: tealTint,
    },
    draftTitle: {
      fontSize: 10,
      fontFamily: bodyFont,
      fontWeight: "bold",
      color: teal,
      marginBottom: 8,
    },
    userQ: {
      fontFamily: bodyFont,
      fontWeight: "bold",
      color: blue,
      fontSize: 10,
      marginTop: 14,
      marginBottom: 3,
    },
    asstBlock: {
      borderLeftWidth: 2,
      borderLeftColor: line,
      paddingLeft: 10,
      marginBottom: 6,
    },
    h1: {
      fontSize: 14,
      fontFamily: bodyFont,
      fontWeight: "bold",
      marginTop: 10,
      marginBottom: 4,
    },
    h2: {
      fontSize: 12,
      fontFamily: bodyFont,
      fontWeight: "bold",
      marginTop: 8,
      marginBottom: 3,
    },
    h3: {
      fontSize: 11,
      fontFamily: bodyFont,
      fontWeight: "bold",
      marginTop: 6,
      marginBottom: 2,
    },
    h4: {
      fontSize: 10,
      fontFamily: bodyFont,
      fontWeight: "bold",
      marginTop: 4,
      marginBottom: 2,
    },
    para: { marginBottom: 3 },
    li: { marginBottom: 2, paddingLeft: 10 },
    hr: {
      borderBottomWidth: 0.75,
      borderBottomColor: line,
      marginTop: 14,
      marginBottom: 14,
    },
    table: { marginTop: 8, marginBottom: 10 },
    tHeaderRow: {
      flexDirection: "row",
      backgroundColor: teal,
      borderWidth: 0.5,
      borderColor: tealDark,
    },
    tRow: {
      flexDirection: "row",
      borderLeftWidth: 0.5,
      borderRightWidth: 0.5,
      borderBottomWidth: 0.5,
      borderColor: line,
    },
    tRowAlt: {
      flexDirection: "row",
      borderLeftWidth: 0.5,
      borderRightWidth: 0.5,
      borderBottomWidth: 0.5,
      borderColor: line,
      backgroundColor: tealTint,
    },
    tCell: {
      flex: 1,
      paddingTop: 4,
      paddingBottom: 4,
      paddingLeft: 8,
      paddingRight: 8,
      borderRightWidth: 0.5,
      borderRightColor: line,
    },
    tCellLast: {
      flex: 1,
      paddingTop: 4,
      paddingBottom: 4,
      paddingLeft: 8,
      paddingRight: 8,
    },
    tHeaderText: {
      fontFamily: bodyFont,
      fontWeight: "bold",
      fontSize: 9,
      color: white,
    },
    tCellText: { fontSize: 9 },
    draftRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 6,
    },
    draftTagAccepted: { fontSize: 7, color: teal },
    draftTagEdited: { fontSize: 7, color: amber },
    draftContent: { fontSize: 9, lineHeight: 1.5, color: text },
  });
};
